// ---------------------------------------------------------------------------
// The overlay stack - who is on top, and therefore who Escape belongs to.
//
// Every panel that sits above the page registers here while it is open: a
// dialog, a drawer, a ticket preview, the guard that asks about unsaved
// changes. Escape goes to the TOPMOST one and nobody else, and a click on a
// backdrop reaches only the backdrop that was clicked. Without this, two
// dialogs open at once both heard Escape - the picker AND the editor under
// it closed together, and the edits went with them.
//
// One rule, one place. A panel that wants the behaviour calls
// `useOverlayDismiss` and does not write a key listener of its own.
// ---------------------------------------------------------------------------

import { useEffect, useRef, type MouseEvent } from 'react';

const stack: symbol[] = [];

/// How many overlays are open right now. For tests, and for nothing else.
export function openOverlayCount(): number {
  return stack.length;
}

/// Register an open overlay and hand it Escape while it is the topmost one.
///
/// `onDismiss` is read through a ref, so the listener always calls the
/// latest callback without the effect depending on its identity - a caller
/// writing `onDismiss={() => setOpen(false)}` re-renders on every keystroke,
/// and tearing the listener down for that is how focus bugs start.
export function useOverlayDismiss(open: boolean, onDismiss: () => void): { isTop: () => boolean } {
  const token = useRef<symbol | null>(null);
  if (token.current === null) token.current = Symbol('overlay');

  const latest = useRef(onDismiss);
  useEffect(() => {
    latest.current = onDismiss;
  });

  useEffect(() => {
    if (!open) return;
    const id = token.current!;
    stack.push(id);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Somebody above us is the one being asked.
      if (stack[stack.length - 1] !== id) return;
      event.stopPropagation();
      event.preventDefault();
      latest.current();
    };

    // Capture phase, so a field inside the panel cannot swallow the key
    // before the panel hears it.
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      const at = stack.lastIndexOf(id);
      if (at >= 0) stack.splice(at, 1);
    };
  }, [open]);

  return {
    isTop: () => stack[stack.length - 1] === token.current,
  };
}

/// A click handler for a backdrop element: fires only when the backdrop
/// ITSELF was clicked, never for a click that bubbled up from the panel
/// inside it - so clicking a field, a button or a dropdown inside the panel
/// is never mistaken for clicking outside.
export function onBackdropClick(dismiss: () => void) {
  return (event: MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) dismiss();
  };
}
